import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Spinner,
  Alert
} from 'reactstrap';
import eventApi from '../../../../api/CheckInEvent';

const toDateInputValue = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  return dateString;
};

const toDateTimeLocalValue = (dateTimeString) => {
  if (!dateTimeString) return '';
  if (dateTimeString.length >= 16) {
    return dateTimeString.slice(0, 16);
  }
  try {
    const parsed = new Date(dateTimeString);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 16);
  } catch (error) {
    console.warn('⚠️ Unable to parse datetime string:', error);
    return '';
  }
};

const mapEventToFormData = (event) => ({
  title: event?.title || '',
  date: toDateInputValue(event?.date),
  location: event?.location || '',
  organizer: event?.organizer || '',
  type: event?.type || 'offline',
  descriptionText: event?.description?.text || event?.descriptionText || '',
  startTime: toDateTimeLocalValue(event?.startTime),
  endTime: toDateTimeLocalValue(event?.endTime),
  maxAttendees:
    event?.maxAttendees === 0 ||
    event?.maxAttendees
      ? String(event.maxAttendees)
      : '',
  status: event?.status || '',
  link: event?.link || '',
  eventImages: [],
  descriptionImages: []
});

const extractEventImageUrls = (event) => {
  if (!event) return [];

  if (Array.isArray(event.img) && event.img.length > 0) {
    return event.img
      .map((item) => (typeof item === 'string' ? item : item?.url))
      .filter(Boolean);
  }

  if (Array.isArray(event.eventImages)) {
    return event.eventImages.filter(Boolean);
  }

  if (typeof event.eventImages === 'string' && event.eventImages) {
    return [event.eventImages];
  }

  return [];
};

const extractDescriptionImageUrls = (event) => {
  if (!event) return [];

  const urls = [];

  if (Array.isArray(event?.description?.media)) {
    event.description.media.forEach((media) => {
      if (media?.type === 'image' && media.url) {
        urls.push(media.url);
      } else if (!media?.type && media?.url) {
        urls.push(media.url);
      }
    });
  }

  if (Array.isArray(event.descriptionImages)) {
    urls.push(...event.descriptionImages.filter(Boolean));
  }

  return urls;
};

const revokeObjectUrls = (urls) => {
  if (!Array.isArray(urls)) return;
  urls.forEach((url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};

const EditEventModal = ({ isOpen, toggle, event, onEventUpdated }) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const initialFormState = useMemo(() => mapEventToFormData(event), [event]);
  const [formData, setFormData] = useState(initialFormState);
  const [previewImages, setPreviewImages] = useState({
    eventImages: [],
    descriptionImages: []
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState);
      setError(null);
      setPreviewImages((prev) => {
        revokeObjectUrls(prev.eventImages);
        revokeObjectUrls(prev.descriptionImages);
        return {
        eventImages: extractEventImageUrls(event),
        descriptionImages: extractDescriptionImageUrls(event)
        };
      });
    }
  }, [initialFormState, isOpen]);

  useEffect(() => {
    return () => {
      revokeObjectUrls(previewImages.eventImages);
      revokeObjectUrls(previewImages.descriptionImages);
    };
  }, [previewImages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const fileArray = Array.from(files);
    setFormData((prev) => ({
      ...prev,
      [name]: fileArray
    }));

    setPreviewImages((prev) => ({
      ...prev,
      ...(fileArray.length
        ? {
            [name]: (() => {
              revokeObjectUrls(prev[name]);
              return fileArray.map((file) => URL.createObjectURL(file));
            })()
          }
        : {})
    }));
  };

  const handleToggle = () => {
    if (!updating) {
      setFormData(initialFormState);
      setError(null);
      toggle();
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!event || !event.id) {
      setError('Không xác định được sự kiện để cập nhật.');
      return;
    }

    if (!formData.title || !formData.date || !formData.location) {
      setError('Vui lòng điền đầy đủ thông tin bắt buộc (Tiêu đề, Ngày, Địa điểm).');
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      const eventData = {
        title: formData.title,
        date: formData.date,
        location: formData.location,
        organizer: formData.organizer,
        type: formData.type,
        descriptionText: formData.descriptionText,
        startTime: formData.startTime,
        endTime: formData.endTime,
        maxAttendees: formData.maxAttendees,
        link: formData.link
      };

      if (formData.status && formData.status !== 'unknown') {
        eventData.status = formData.status;
      }

      const cleanedEventData = Object.entries(eventData).reduce((acc, [key, value]) => {
        if (value === null || value === undefined) {
          return acc;
        }
        if (typeof value === 'string' && value.trim() === '') {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});

      if (formData.eventImages && formData.eventImages.length > 0) {
        cleanedEventData.eventImages = formData.eventImages;
      }

      if (formData.descriptionImages && formData.descriptionImages.length > 0) {
        cleanedEventData.descriptionImages = formData.descriptionImages;
      }

      await eventApi.update(event.id, cleanedEventData);
      alert('Cập nhật sự kiện thành công!');

      if (onEventUpdated) {
        onEventUpdated();
      } else {
        handleToggle();
      }
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err?.response?.data?.message || err.message || 'Lỗi không xác định');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle}>Chỉnh sửa sự kiện</ModalHeader>
      {!event ? (
        <ModalBody>
          <Alert color="warning" className="mb-0">
            Không tìm thấy dữ liệu sự kiện để chỉnh sửa.
          </Alert>
        </ModalBody>
      ) : (
        <Form onSubmit={handleUpdateEvent}>
          <ModalBody>
            {error && (
              <Alert color="danger">
                {error}
              </Alert>
            )}
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Tiêu đề sự kiện *</Label>
                  <Input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Nhập tiêu đề sự kiện"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Ngày tổ chức *</Label>
                  <Input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Thời gian bắt đầu</Label>
                  <Input
                    type="datetime-local"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Thời gian kết thúc</Label>
                  <Input
                    type="datetime-local"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Địa điểm *</Label>
                  <Input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Nhập địa điểm tổ chức"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Đơn vị tổ chức</Label>
                  <Input
                    type="text"
                    name="organizer"
                    value={formData.organizer}
                    onChange={handleInputChange}
                    placeholder="Nhập tên đơn vị tổ chức"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Loại sự kiện</Label>
                  <Input
                    type="select"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="offline">Offline</option>
                    <option value="online">Online</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Trạng thái</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="">Giữ nguyên trạng thái</option>
                    <option value="upcoming">Sắp diễn ra</option>
                    <option value="now">Đang diễn ra</option>
                    <option value="ended">Đã kết thúc</option>
                    <option value="unknown">Chưa xác định</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Số lượng người tham gia</Label>
                  <Input
                    type="number"
                    name="maxAttendees"
                    value={formData.maxAttendees}
                    onChange={handleInputChange}
                    placeholder="1000"
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Mô tả sự kiện</Label>
                  <Input
                    type="textarea"
                    name="descriptionText"
                    value={formData.descriptionText}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Nhập mô tả chi tiết về sự kiện"
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Link (Zalo, Website...)</Label>
                  <Input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleInputChange}
                    placeholder="https://zalo.me/..."
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Ảnh sự kiện (tải mới nếu muốn cập nhật)</Label>
                  <Input
                    type="file"
                    name="eventImages"
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple
                  />
                  <small className="text-muted">Nếu bỏ trống sẽ giữ nguyên ảnh hiện tại.</small>
                  {previewImages.eventImages.length > 0 && (
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      {previewImages.eventImages.map((src, index) => (
                        <img
                          key={`${src}-${index}`}
                          src={src}
                          alt="Ảnh sự kiện"
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '0.5rem' }}
                        />
                      ))}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Ảnh mô tả (tải mới nếu muốn cập nhật)</Label>
                  <Input
                    type="file"
                    name="descriptionImages"
                    onChange={handleFileChange}
                    accept="image/*"
                    multiple
                  />
                  <small className="text-muted">Có thể chọn nhiều ảnh, bỏ trống để giữ nguyên.</small>
                  {previewImages.descriptionImages.length > 0 && (
                    <div className="mt-2 d-flex gap-2 flex-wrap">
                      {previewImages.descriptionImages.map((src, index) => (
                        <img
                          key={`${src}-${index}`}
                          src={src}
                          alt="Ảnh mô tả"
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '0.5rem' }}
                        />
                      ))}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleToggle} disabled={updating}>
              Hủy
            </Button>
            <Button color="primary" type="submit" disabled={updating}>
              {updating ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Đang lưu...
                </>
              ) : (
                'Lưu thay đổi'
              )}
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Modal>
  );
};

export default EditEventModal;


